import { JSX } from "react";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { SearchBox } from "../SearchBox/SearchBox";

export class TimeZonePicker extends BaseComponent<ITimeZonePickerProps, ITimeZonePickerState>
{
    constructor(props: ITimeZonePickerProps)
    {
        super(props);
        this.state = {
            searchText: '',
            timeZoneOptions: [],
            filteredTimeZoneOptions: []
        };
    }

    public async componentDidMount(): Promise<void>
    {
        await this.loadTimeZones();
        const timeZone = this.findTimeZone(this.props.value, true);
        (timeZone == null ? this.props.onChange('') : this.setState({ searchText: timeZone }));
    }

    public componentDidUpdate(prevProps: ITimeZonePickerProps): void
    {
        if (this.props.value !== prevProps.value)
        {
            const timeZone = this.findTimeZone(this.props.value, true);
            (timeZone == null ? this.props.onChange('') : this.setState({ searchText: timeZone }));
        }
    }

    private async loadTimeZones(): Promise<void>
    {
        const timeZones = Intl.supportedValuesOf('timeZone').sort((a, b) => a > b ? 1 : -1);
        await this.setStateAsync({ timeZoneOptions: timeZones, filteredTimeZoneOptions: timeZones });
    }

    private findTimeZone(value: string, relaxed: boolean): string | null
    {
        if (value === '')
        {
            return '';
        }

        const lowercaseValue = value.toLowerCase();
        if (relaxed)
        {
            const candidateTimeZones = this.state.timeZoneOptions.filter(i => i.toLowerCase().includes(lowercaseValue));
            const timeZone = (candidateTimeZones.length === 1 ? candidateTimeZones[0] : null);
            return timeZone;
        }
        else
        {
            const timeZone = this.state.timeZoneOptions.find(i => i.toLowerCase() === lowercaseValue) ?? null;
            return timeZone;
        }
    }

    private handleSearchTextChange(value: string): void
    {
        if (value.length === 0)
        {
            // trigger the onChange when the searchbox is cleared
            this.props.onChange('');
            return;
        }
        this.setState({ searchText: value, filteredTimeZoneOptions: this.state.timeZoneOptions.filter(x => x.toLowerCase().includes(value.toLowerCase())).slice(0, 10) })
        const timeZone = this.findTimeZone(value, false);
        (timeZone == null ? this.setState({ searchText: value }) : this.props.onChange(timeZone));
    }

    private handleBlur(): void
    {
        const timeZone = this.findTimeZone(this.state.searchText, true) ?? '';
        this.setState({ searchText: timeZone });
        this.props.onChange(timeZone);
    }

    public render(): JSX.Element
    {
        return (
            <SearchBox
                items={this.state.filteredTimeZoneOptions}
                onInputValueChange={e => this.handleSearchTextChange(e.inputValue)}
                onBlur={_ => this.handleBlur()}
                disabled={this.props.disabled}
                placeholder={this.props.placeholder}
                label={this.props.label}
                required={this.props.required}
                requiredLabel={this.props.requiredLabel}
                invalid={this.props.invalid}
                invalidMessage={this.props.invalidMessage}
                inputValue={this.state.searchText}
            />
        );
    }
}

export interface ITimeZonePickerProps
{
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    invalid?: boolean;
    invalidMessage?: string;
    requiredLabel?: string;
    value: string;
    onChange: (value: string) => void;
}

export interface ITimeZonePickerState
{
    searchText: string;
    timeZoneOptions: string[];
    filteredTimeZoneOptions: string[];
}
